import Vue from 'vue';
import Router from 'vue-router';
import Home from './views/Home.vue';
import Login from './views/Login.vue';
import Register from './views/Register.vue';
import i18n from './i18n';

Vue.use(Router);

export const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home
    },
    {
      path: '/login',
      name: 'login',
      component: Login
    },
    {
      path: '/register',
      name: 'register',
      component: Register
    },
    {
      path: '/profile',
      name: 'profile',
      // lazy-loaded
      component: () => import('./views/Profile.vue'),
      meta: { breadCrumb: i18n.t('AUTHENTICATION.LBL_PROFILE') },
    },
    {
      path: '*',
      name: 'page404',
      component: () => import('./components/404')
    },
    {
      path: '/notAllowed',
      name: 'notAllowed',
      // lazy-loaded
      component: () => import('./components/405')
    },

    //Institutions components
    {
      path: '/institutionsList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.INSTITUTIONS.LBL_LIST_INSTITUTIONS') },
      children: [
        {
          path: '',
          name: "institutions",
          component: () => import("./components/Institution/InstitutionsList")
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.INSTITUTIONS.LBL_DETAILS_INSTITUTION')},
          children: [
            {
              path: '',
              name: "institution-details",
              component: () => import("./components/Institution/Institution")
            },
            {
              path: 'edit',
              name: "institution-edit",
              component: () => import("./components/Institution/EditInstitution"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.INSTITUTIONS.LBL_UPDATE_INSTITUTION') }
            }
          ]
        }
      ]
    },

    //Sectors components
    {
      path: '/sectorsList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.SECTORS.LBL_LIST_SECTORS') },
      children: [
        {
          path: '',
          name: "sectors",
          component: () => import("./components/Sector/SectorsList")
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.SECTORS.LBL_DETAILS_SECTOR') },
          children: [
            {
              path: '',
              name: "sector-details",
              component: () => import("./components/Sector/Sector")
            },
            {
              path: 'edit',
              name: "sector-edit",
              component: () => import("./components/Sector/EditSector"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.SECTORS.LBL_UPDATE_SECTOR') }
            }
          ]
        }
      ]
    },

    //Users components
    {
      path: '/usersList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.USERS.LBL_LIST_USERS') },
      children: [
        {
          path: '',
          name: "users",
          component: () => import("./components/User/UsersList")
        },
        {
          path: 'new',
          name: "user-create",
          component: () => import("./components/User/CreateUser"),
          meta: { breadCrumb: i18n.t('BREADCRUMBS.USERS.LBL_CREATE_USER') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.USERS.LBL_DETAILS_USER') },
          children: [
            {
              path: '',
              name: "user-details",
              component: () => import("./components/User/User")
            },
            {
              path: 'edit',
              name: "user-edit",
              component: () => import("./components/User/EditUser"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.USERS.LBL_UPDATE_USER') }
            }
          ]
        }

      ]
    },

    //Subjects components
    {
      path: '/subjectsList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.SUBJECTS.LBL_LIST_SUBJECTS') },
      children: [
        {
          path: '',
          name: "subjects",
          component: () => import("./components/Subject/SubjectsList")
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.SUBJECTS.LBL_DETAILS_SUBJECT')},
          children: [
            {
              path: '',
              name: "subject-details",
              component: () => import("./components/Subject/Subject")
            }
          ]
        }
      ]
    },

    //Subject subsidiaries components
    {
      path: '/subjectSubsidiariesList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.SUBJECT_SUBSIDIARIES.LBL_LIST_SUBJECT_SUBSIDIARIES') },
      children: [
        {
          path: '',
          name: "subjectSubsidiaries",
          component: () => import("./components/Subject/SubjectSubsidiariesList")
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.SUBJECT_SUBSIDIARIES.LBL_DETAILS_SUBJECT_SUBSIDIARY')},
          children: [
            {
              path: '',
              name: "subjectSubsidiary-details",
              component: () => import("./components/Subject/SubjectSubsidiary")
            }
          ]
        }
      ]
    },

    //Checklist components
    {
      path: '/checklistsList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.CHECKLISTS.LBL_LIST_CHECKLISTS') },
      children: [
        {
          path: '',
          name: "checklists",
          component: () => import("./components/Checklist/ChecklistsList")
        },
        {
          path: 'new',
          name: "checklist-create",
          component: () => import("./components/Checklist/CreateChecklist"),
          meta: { breadCrumb: i18n.t('BREADCRUMBS.CHECKLISTS.LBL_CREATE_CHECKLIST') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.CHECKLISTS.LBL_DETAILS_CHECKLIST')},
          children: [
            {
              path: '',
              name: "checklist-details",
              component: () => import("./components/Checklist/Checklist")
            },
            {
              path: 'edit',
              name: "checklist-edit",
              component: () => import("./components/Checklist/EditChecklist"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.CHECKLISTS.LBL_UPDATE_CHECKLIST') }
            }
          ]
        }
      ]
    },

   // IT Help Desk components
    {
      path: '/tickets',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.TICKETS.LBL_LIST_TICKETS') },
      children: [
        {
          path: '',
          name: "tickets",
          component: () => import("./components/ITHelpDesk/TicketsList")
        },
        {
          path: 'new',
          name: "ticket-create",
          component: () => import("./components/ITHelpDesk/CreateTicket"),
          meta: { breadCrumb: i18n.t('BREADCRUMBS.TICKETS.LBL_CREATE_TICKET') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.TICKETS.LBL_DETAILS_TICKET') },
          children: [
            {
              path: '',
              name: "ticket-details",
              component: () => import("./components/ITHelpDesk/Ticket")
            },
            {
              path: 'edit',
              name: "ticket-edit",
              component: () => import("./components/ITHelpDesk/EditTicket"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.TICKETS.LBL_UPDATE_TICKET') }
            }
          ]
        },
      ]
    },

    //Reports components
    {
      path: "/reports",
      // alias: "/reports",
      name: "reports",
      component: () => import("./components/ITHelpDesk/Report/ReportsList")
    },

    //Risks components
    {
      path: '/risksList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.RISKS.LBL_LIST_RISKS') },
      children: [
        {
          path: '',
          name: "risks",
          component: () => import("./components/Risk/RisksList")
        },
        {
          path: 'new',
          name: "risk-create",
          component: () => import("./components/Risk/CreateRisk"),
          meta: { breadCrumb: i18n.t('BREADCRUMBS.RISKS.LBL_CREATE_RISK') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.RISKS.LBL_DETAILS_RISK') },
          children: [
            {
              path: '',
              name: "risk-details",
              component: () => import("./components/Risk/Risk")
            },
            {
              path: 'edit',
              name: "risk-edit",
              component: () => import("./components/Risk/EditRisk"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.RISKS.LBL_UPDATE_RISK') }
            }
          ]
        }

      ]
    },

    //Risk Specific Elements components
    {
        path: '/riskSpecificElementsList',
        component: {
            render (c) {
                return c('router-view')
            }
        },
        meta: { breadCrumb: i18n.t('BREADCRUMBS.RISK_SPECIFIC_ELEMENTS.LBL_LIST_SPECIFIC_ELEMENTS') },
        children: [
            {
                path: '',
                name: "riskSpecificElements",
                component: () => import("./components/Risk/SpecificElement/RiskSpecificElementsList")
            },
            {
                path: 'new',
                name: "riskSpecificElement-create",
                component: () => import("./components/Risk/SpecificElement/CreateRiskSpecificElement"),
                meta: { breadCrumb: i18n.t('BREADCRUMBS.RISK_SPECIFIC_ELEMENTS.LBL_CREATE_SPECIFIC_ELEMENT') }
            },
            {
                path: ':id',
                component: {
                    render (c) {
                        return c('router-view')
                    }
                },
                meta: { breadCrumb: i18n.t('BREADCRUMBS.RISK_SPECIFIC_ELEMENTS.LBL_DETAILS_SPECIFIC_ELEMENT') },
                children: [
                    {
                        path: '',
                        name: "riskSpecificElement-details",
                        component: () => import("./components/Risk/SpecificElement/RiskSpecificElement")
                    },
                    {
                        path: 'edit',
                        name: "riskSpecificElement-edit",
                        component: () => import("./components/Risk/SpecificElement/EditRiskSpecificElement"),
                        meta: { breadCrumb: i18n.t('BREADCRUMBS.RISK_SPECIFIC_ELEMENTS.LBL_UPDATE_SPECIFIC_ELEMENT') }
                    }
                ]
            }

        ]
    },

    //Risk Criteria components
    {
      path: '/riskCriteriaList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.RISK_CRITERIA.LBL_LIST_CRITERIA') },
      children: [
        {
          path: '',
          name: "riskCriteria",
          component: () => import("./components/Risk/Criterion/RiskCriteriaList")
        },
        {
          path: 'new',
          name: "riskCriterion-create",
          component: () => import("./components/Risk/Criterion/CreateRiskCriterion"),
          meta: { breadCrumb: i18n.t('BREADCRUMBS.RISK_CRITERIA.LBL_CREATE_CRITERION') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.RISK_CRITERIA.LBL_DETAILS_CRITERION') },
          children: [
            {
              path: '',
              name: "riskCriterion-details",
              component: () => import("./components/Risk/Criterion/RiskCriterion")
            },
            {
              path: 'edit',
              name: "riskCriterion-edit",
              component: () => import("./components/Risk/Criterion/EditRiskCriterion"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.RISK_CRITERIA.LBL_UPDATE_CRITERION') }
            }
          ]
        }

      ]
    },
    //Laws components
    {
      path: '/lawsList',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.LAWS.LBL_LIST_LAWS') },
      children: [
        {
          path: '',
          name: "laws",
          component: () => import("./components/Law/LawsList")
        },
        {
          path: 'new',
          name: "law-create",
          component: () => import("./components/Law/CreateLaw"),
          meta: { breadCrumb: i18n.t('BREADCRUMBS.LAWS.LBL_CREATE_LAW') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.LAWS.LBL_DETAILS_LAW') },
          children: [
            {
              path: '',
              name: "law-details",
              component: () => import("./components/Law/Law")
            },
            {
              path: 'edit',
              name: "law-edit",
              component: () => import("./components/Law/EditLaw"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.LAWS.LBL_UPDATE_LAW') }
            }
          ]
        }

      ]
    },

      //Weight surveillance
    {
      path: '/weightSurveillance',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.WEIGHT_SURVEILLANCE.LBL_WEIGHT_SURVEILLANCE') },
      children: [
        {
          path: '',
          name: "weightSurveillance",
          component: () => import("./components/WeightSurveillance/WeightSurveillanceList")
        },
            {
              path: ':id',
              name: "weightSurveillance-edit",
              component: () => import("./components/WeightSurveillance/EditWeightSurveillance"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.WEIGHT_SURVEILLANCE.LBL_WEIGHT_SURVEILLANCE') }
            }
      ]
    },


    // Target Plans components
      //Yearly Plans
    {
      path: '/yearlyPlans',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.TARGET_PLANS.LBL_LIST_YEARLY_PLANS') },
      children: [
        {
          path: '',
          name: "yearlyPlans",
          component: () => import("./components/TargetPlan/YearlyPlansList")
        },
        {
          path: 'new',
          name: "yearlyPlan-create",
          component: () => import("./components/TargetPlan/CreateYearlyPlan"),
          meta: { breadCrumb: i18n.t('BREADCRUMBS.TARGET_PLANS.LBL_CREATE_YEARLY_PLAN') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.TARGET_PLANS.LBL_DETAILS_YEARLY_PLAN') },
          children: [
            {
              path: '',
              name: "yearlyPlan-details",
              component: () => import("./components/TargetPlan/YearlyPlan")
            },
            {
              path: 'edit',
              name: "yearlyPlan-edit",
              component: () => import("./components/TargetPlan/EditYearlyPlan"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.TARGET_PLANS.LBL_UPDATE_YEARLY_PLAN') }
            }
          ]
        },
      ]
    },

      //Monthly Plans
    {
      path: '/monthlyPlans',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.TARGET_PLANS.LBL_LIST_MONTHLY_PLANS') },
      children: [
        {
          path: '',
          name: "monthlyPlans",
          component: () => import("./components/TargetPlan/MonthlyPlansList")
        },
        {
          path: 'new',
          name: "monthlyPlan-create",
          component: () => import("./components/TargetPlan/CreateMonthlyPlan"),
          meta: { breadCrumb: i18n.t('BREADCRUMBS.TARGET_PLANS.LBL_CREATE_MONTHLY_PLAN') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          meta: { breadCrumb: i18n.t('BREADCRUMBS.TARGET_PLANS.LBL_DETAILS_MONTHLY_PLAN') },
          children: [
            {
              path: '',
              name: "monthlyPlan-details",
              component: () => import("./components/TargetPlan/MonthlyPlan")
            },
            {
              path: 'edit',
              name: "monthlyPlan-edit",
              component: () => import("./components/TargetPlan/EditMonthlyPlan"),
              meta: { breadCrumb: i18n.t('BREADCRUMBS.TARGET_PLANS.LBL_UPDATE_MONTHLY_PLAN') }
            }
          ]
         },
      ]
    },

    //Inspection cases
    {
      path: '/inspectionCases',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_CASES.LBL_LIST_INSPECTION_CASES') },
      children: [
        {
          path: '',
          name: "inspectionCases",
          component: () => import("./components/InspectionProcedure/InspectionCase/InspectionCasesList")
        },
        {
          path: 'subject-data',
          name: "avrm-employee-subject-details",
          component: () => import("./components/InspectionProcedure/Avrm/EmployeeSubjectData"),
        },
        {
          path: 'customs-administration-data',
          name: "customs-administration-list",
          component: () => import("./components/InspectionProcedure/Curm/CustomsAdministrationData"),
        },
        {
          path: ':mrn',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: '',
              name: "customs-administration-details",
              component: () => import("./components/InspectionProcedure/Curm/MrnDetails")
            },
          ]
        },
        // {
        //   path: ':id',
        //   component: {
        //     render (c) {
        //       return c('router-view')
        //     }
        //   },
        //   meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_CASES.LBL_DETAILS_INSPECTION_CASE') },
        //   children: [
        //     {
        //       path: '',
        //       name: "inspectionCase-details",
        //       component: () => import("./components/InspectionProcedure/InspectionCase/InspectionCase")
        //     },
        //     {
        //       path: 'edit',
        //       name: "inspectionCase-edit",
        //       component: () => import("./components/InspectionProcedure/InspectionCase/EditInspectionCase"),
        //       meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_CASES.LBL_UPDATE_INSPECTION_CASE') }
        //     }
        //   ]
        // },
      ]
    },
    //Inspection work orders
    {
      path: '/inspectionWorkOrders',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_WORK_ORDERS.LBL_LIST_INSPECTION_WORK_ORDERS') },
      children: [
        {
          path: '',
          name: "inspectionWorkOrders",
          component: () => import("./components/InspectionProcedure/InspectionWorkOrder/InspectionWorkOrdersList")
        },
        {
          path: 'new',
          name: "inspectionWorkOrder-create",
          component: () => import("./components/InspectionProcedure/InspectionWorkOrder/CreateInspectionWorkOrder"),
          // meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_WORK_ORDERS.LBL_CREATE_INSPECTION_WORK_ORDER') }
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          // meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_WORK_ORDERS.LBL_DETAILS_INSPECTION_WORK_ORDER') },
          children: [
            {
              path: '',
              name: "inspectionWorkOrder-details",
              component: () => import("./components/InspectionProcedure/InspectionWorkOrder/InspectionWorkOrder")
            },
            {
              path: 'edit',
              name: "inspectionWorkOrder-edit",
              component: () => import("./components/InspectionProcedure/InspectionWorkOrder/EditInspectionWorkOrder"),
              // meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_WORK_ORDERS.LBL_UPDATE_INSPECTION_WORK_ORDER') }
            }     
          ]
        },

      ]
    },
    //Joint Work Orders
    {
      path: '/jointWorkOrders',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_WORK_ORDERS.LBL_LIST_INSPECTION_WORK_ORDERS') },
      children: [
        {
          path: '',
          name: "inspectionWorkOrders",
          component: () => import("./components/InspectionProcedure/InspectionWorkOrder/InspectionWorkOrdersList")
        },
        {
          path: 'new',
          name: "jointWorkOrder-create",
          component: () => import("./components/InspectionProcedure/JointWorkOrder/CreateJointWorkOrder"),
        },
        {
          path: ':id',
          component: {
            render (c) {
              return c('router-view')
            }
          },
          children: [
            {
              path: '',
              name: "jointWorkOrder-details",
              component: () => import("./components/InspectionProcedure/JointWorkOrder/JointWorkOrder")
            },
            {
              path: 'edit',
              name: "jointWorkOrder-edit",
              component: () => import("./components/InspectionProcedure/JointWorkOrder/EditJointWorkOrder")
            }
            
          ]
        },

      ]
    },


    //Inspection reports
    {
      path: '/inspectionReports',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_REPORTS.LBL_LIST_INSPECTION_REPORTS') },
      children: [
        {
          path: '',
          name: "inspectionReports",
          component: () => import("./components/InspectionProcedure/InspectionReport/InspectionReportsList")
        }
      ]
    },

    //Inspection conclusions
    {
      path: '/inspectionConclusions',
      component: {
        render (c) {
          return c('router-view')
        }
      },
      meta: { breadCrumb: i18n.t('BREADCRUMBS.INSPECTION_CONCLUSIONS.LBL_LIST_INSPECTION_CONCLUSIONS') },
      children: [
        {
          path: '',
          name: "inspectionConclusions",
          component: () => import("./components/InspectionProcedure/InspectionConclusion/InspectionConclusionsList")
        }
      ]
    },
    //UserGuidance components
    {
      path: '/userGuidance',
      component: {
        render(c){
          return c('router-view')
        }
      },
      children: [
        {
          path: '',
          name: "userGuidance",
          component: () => import("./components/UserGuidance/UserGuidance")
        }
      ]
    }
  ]
});


router.beforeEach((to, from, next) => {
  const publicPages = ['/login', '/register', '/home'];
  const authRequired = !publicPages.includes(to.path);
  const loggedIn = localStorage.getItem('user');

  // trying to access a restricted page + not logged in
  // redirect to login page
  if (authRequired && !loggedIn) {
    next('/login');
  } else {
    next();
  }
});
